import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams, useSearchParams } from "react-router-dom";

import { useApi } from "shared/services/api/useApi";

const useAccessGroupPageAccessManagePermissions = () => {
    const { getDataAsync, postData, deleteData } = useApi();
    const queryClient = useQueryClient();
    const { groupId } = useParams();
    const [searchParams] = useSearchParams();
    const pageId = searchParams.get("pageId");

    const getPageAccessPageActionDetailsApiCall = useCallback(
        async () => {
            const response = await getDataAsync(
                "accessGroupAccessPageActionDetails",
                {
                    urlParams: [
                        { name: "$groupId", value: groupId },
                        { name: "$pageId", value: pageId },
                    ],
                }
            );

            const result = response.status === 204
                ? null
                : response.json();

            return result;
        },
        [getDataAsync, groupId, pageId]
    );

    const createAccessGroupPageAccessPageActionApiCall = useCallback(
        ({ code }) => {
            return postData("accessGroupAccessPageActionManage", {
                urlParams: [
                    { name: "$code", value: code },
                    { name: "$groupId", value: groupId },
                    { name: "$pageId", value: pageId },
                ],
            });
        },
        [postData, groupId, pageId]
    );

    const deleteAccessGroupPageAccessPageActionApiCall = useCallback(
        ({ code }) => {
            return deleteData("accessGroupAccessPageActionManage", {
                urlParams: [
                    { name: "$code", value: code },
                    { name: "$groupId", value: groupId },
                    { name: "$pageId", value: pageId },
                ],
            });
        },
        [deleteData, groupId, pageId]
    );

    const mutationOptions = useMemo(() => {
        return {
            onSuccess: () => {
                queryClient.invalidateQueries([
                    "accessGroupsPageAccessPageActionDetails",
                    groupId,
                    pageId,
                ]);
            },
        };
    }, [queryClient, groupId, pageId]);

    const createAccessGroupPageAccessPageAction = useMutation(
        createAccessGroupPageAccessPageActionApiCall,
        mutationOptions
    );

    const deleteAccessGroupPageAccessPageAction = useMutation(
        deleteAccessGroupPageAccessPageActionApiCall,
        mutationOptions
    );

    return useMemo(() => {
        const useAccessGroupPageAccessPageActionDetails = () =>
            useQuery(
                ["accessGroupsPageAccessPageActionDetails", groupId, pageId],
                () => getPageAccessPageActionDetailsApiCall({ groupId, pageId })
            );

        return {
            useAccessGroupPageAccessPageActionDetails,
            createAccessGroupPageAccessPageAction,
            deleteAccessGroupPageAccessPageAction,
        };
    }, [
        getPageAccessPageActionDetailsApiCall,
        createAccessGroupPageAccessPageAction,
        deleteAccessGroupPageAccessPageAction,
        groupId,
        pageId,
    ]);
};

export default useAccessGroupPageAccessManagePermissions;
